import CopyToClipboard from '@bumble/app/components/CopyToClipboard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { 
  Box,
  Button, 
  Card, 
  CardContent, 
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CardRows } from '../components/CardRows';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';

export const Operations = () => {
  const api = useApi();

  const [webhook, setWebhook] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [downEventEmail, setDownEventEmail] = useState<string>('');
  const [key, setKey] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const infoURL = 'https://www.bumblebeenet.com/post/bumblebee-platform-webhook-configuration-example';

  const { isLoading, data: opsData } = useQuery(
    ['getAccountOps'],
    api.getAccountOpsApiV1GetAccountOpsGet.bind(null, {}),
    {
      enabled: true,
      cacheTime: 0,
      staleTime: 0,
      refetchOnMount: true,
      initialData: undefined,
    }
  );

  useEffect(() => {
    setWebhook(opsData?.data[0]?.webhook_url || '');
    setEmail(opsData?.data[0]?.email || '');
    setDownEventEmail(opsData?.data[0].down_event_email || '');
    setKey(opsData?.data[0]?.signature_key || '');
  }, [opsData]);

  const setAccountOps = useMutation(api.setAccountOpsApiV1SetAccountOpsPost, {
    onSuccess(data) {
      toast.success('Account Operations updated.');
      setKey(data?.data?.signature_key);
      setLoading(false);
    },
    onError: apiError,
  });

  const handleAccountOpsSubmit = () => {
    setLoading(true);
    setAccountOps.mutate({
      webhook_url: webhook,
      email: email,
      down_event_email: downEventEmail,
    });
  };

  const isDisabled = () => loading;

  const changeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setLoading(false);
  }

  const changeDownEventEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDownEventEmail(e.target.value);
    setLoading(false);
  }

  const changeURL = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebhook(e.target.value);
    setLoading(false);
  }

  if (isLoading) {
    return <Loading />;
  }
  return (
    <PageContent>
      <PageHeader pageName="Operations" />
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">Event Notifications</Typography>
              <Typography variant="body2" color="textSecondary">
                Configure email and webhook URL to receive event notifications.
              </Typography>
            </CardContent>
            <CardRows
              label="General Events"
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="email"
                  label="Enter your email(s)"
                  placeholder="email@domain.com"
                  variant="outlined"
                  value={email}
                  onChange={changeEmail}
                />
              }
            />
            <CardRows
              label=" Link-Down Events"
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="email"
                  label="Enter your email(s)"
                  placeholder="email@domain.com"
                  variant="outlined"
                  value={downEventEmail}
                  onChange={changeDownEventEmail}
                />
              }
            />
            <CardRows
              label={
                (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">Webhook URL</Typography>
                  <Box
                    component="a"
                    href={infoURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Tooltip arrow title="Info">
                      <IconButton size="small" color="primary">
                        <InfoOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>) as any
              }
              value={
                <TextField
                  fullWidth
                  size="small"
                  id="webhook"
                  label="Enter the webhook URL"
                  placeholder="https://webhook.site/"
                  variant="outlined"
                  value={webhook}
                  onChange={changeURL}
                />
              }
            />
            <Divider />
            <CardRows
              label="Webhook Signature Key"
              value={
                <Box sx={{ position: 'relative', width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Signature Key"
                    variant="outlined"
                    value={key}
                    multiline
                    minRows={2}
                    maxRows={2}
                    disabled
                  />
                  <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 999 }}>
                    <CopyToClipboard content={key} />
                  </Box>
                </Box>
              }
            />
            <Box p={5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                type="submit"
                disabled={isDisabled()}
                onClick={handleAccountOpsSubmit}
              >
                Save
              </Button>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">What are Event Notifications?</Typography>
              <Typography variant="body2" color="textSecondary">
                Event notifications allow you to receive real-time updates about critical system events
                via emails or webhooks. Emails notify you directly, while webhooks can trigger automated
                processes in your applications. (You can enter multiple emails separated by comma)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageContent>
  );
};
